// src/pages/portal/PortalLogin.jsx
//
// Entrada al portal de clientes. Dos modos:
//  - Ingresar: email + contraseña.
//  - Primera vez: el email debe estar autorizado por el laboratorio; el cliente
//    crea su contraseña y confirma por correo (una sola vez).
//
// Si el email NO está autorizado, la cuenta igual se crea pero nace sin acceso
// (rol 'pending'): al entrar ve el aviso de "cuenta no habilitada" del portal.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

export default function PortalLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'error'|'ok', text }

  const handleLogin = async (e) => {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: pass });
      if (error) throw error;
      navigate('/portal', { replace: true });
    } catch (err) {
      const t = String(err?.message || err);
      setMsg({
        type: 'error',
        text: /invalid login credentials/i.test(t)
          ? 'Email o contraseña incorrectos.'
          : /email not confirmed/i.test(t)
            ? 'Tu email todavía no está confirmado: revisá tu correo (también el spam).'
            : `No se pudo ingresar: ${t}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/portal/reset`,
      });
      if (error) throw error;
      setMsg({
        type: 'ok',
        text: 'Si ese email tiene cuenta, te enviamos un link para crear una contraseña nueva. Revisá tu correo (también el spam).',
      });
      setMode('login');
    } catch (err) {
      const t = String(err?.message || err);
      setMsg({
        type: 'error',
        text: /rate limit/i.test(t)
          ? 'Se alcanzó el límite de envíos de correo por ahora. Esperá unos minutos y volvé a intentar.'
          : `No se pudo enviar el correo: ${t}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (pass.length < 8) { setMsg({ type: 'error', text: 'La contraseña debe tener al menos 8 caracteres.' }); return; }
    if (pass !== pass2) { setMsg({ type: 'error', text: 'Las contraseñas no coinciden.' }); return; }
    setBusy(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const { data: authorized, error: authorizationError } = await supabase.rpc(
        'portal_email_is_authorized',
        { p_email: normalizedEmail }
      );
      const authorizationRpcPending =
        authorizationError?.code === 'PGRST202' ||
        /portal_email_is_authorized/i.test(
          String(authorizationError?.message || authorizationError || '')
        );
      if (authorizationError && !authorizationRpcPending) throw authorizationError;
      if (!authorizationRpcPending && !authorized) {
        setMsg({
          type: 'error',
          text: 'Ese email todavía no está habilitado para el portal. Pedile al laboratorio que lo agregue a los correos autorizados de tu empresa.',
        });
        return;
      }

      const { error } = await supabase.auth.signUp({ email: normalizedEmail, password: pass });
      if (error) throw error;
      setMsg({
        type: 'ok',
        text: 'Cuenta creada. Te enviamos un correo para confirmar tu email: abrí el link y después ingresá con tu contraseña.',
      });
      setMode('login');
      setPass(''); setPass2('');
    } catch (err) {
      const t = String(err?.message || err);
      setMsg({
        type: 'error',
        text: /already registered/i.test(t)
          ? 'Ese email ya tiene cuenta: usá "Ingresar" (o pedí el blanqueo al laboratorio).'
          : `No se pudo crear la cuenta: ${t}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-xl bg-slate-800/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50';

  return (
    <div
      className="relative min-h-screen bg-[#0b0f14] text-white px-4 py-10 bg-cover bg-center"
      style={{ backgroundImage: "url('/fondo-login-clientes.webp')" }}
    >
      {/* Velo para que la tarjeta y el texto lean bien sobre la foto (clara) */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-black/75" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-sm">
        <div className="text-center mb-7">
          <div className="text-3xl font-bold tracking-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">Calins</div>
          <div className="text-sm text-slate-200 mt-1 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">Portal de clientes · Certificados de calibración</div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0b0f14]/85 backdrop-blur-md p-5 shadow-2xl">
          {/* Tabs */}
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-white/5 p-1 mb-5">
            {[['login', 'Ingresar'], ['signup', 'Primera vez']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => { setMode(k); setMsg(null); }}
                className={`py-2 rounded-lg text-sm font-medium transition ${mode === k ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {msg && (
            <div className={`mb-4 rounded-lg border px-3 py-2.5 text-sm ${msg.type === 'ok' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-rose-500/30 bg-rose-500/10 text-rose-200'}`}>
              {msg.text}
            </div>
          )}

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-3">
              <input type="email" required autoComplete="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              <input type="password" required autoComplete="current-password" placeholder="Contraseña" value={pass} onChange={(e) => setPass(e.target.value)} className={inputCls} />
              <button type="submit" disabled={busy} className="w-full rounded-xl bg-sky-600 hover:bg-sky-500 transition px-4 py-3 font-semibold disabled:opacity-60">
                {busy ? 'Ingresando…' : 'Ingresar'}
              </button>
              <button
                type="button"
                onClick={() => { setMode('reset'); setMsg(null); }}
                className="block w-full text-center text-xs text-slate-400 hover:text-white underline underline-offset-2 pt-1"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </form>
          ) : mode === 'reset' ? (
            <form onSubmit={handleReset} className="space-y-3">
              <p className="text-xs text-slate-400 leading-relaxed">
                Ingresá tu email y te mandamos un link para crear una contraseña nueva.
              </p>
              <input type="email" required autoComplete="email" placeholder="Tu email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              <button type="submit" disabled={busy} className="w-full rounded-xl bg-sky-600 hover:bg-sky-500 transition px-4 py-3 font-semibold disabled:opacity-60">
                {busy ? 'Enviando…' : 'Enviarme el link'}
              </button>
              <button
                type="button"
                onClick={() => { setMode('login'); setMsg(null); }}
                className="block w-full text-center text-xs text-slate-400 hover:text-white underline underline-offset-2 pt-1"
              >
                Volver a ingresar
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-3">
              <p className="text-xs text-slate-400 leading-relaxed">
                Usá el email que registraste con el laboratorio. Si tu email no está habilitado,
                escribinos a <a className="text-sky-400" href="mailto:laboratorio@calins.com.ar">laboratorio@calins.com.ar</a>.
              </p>
              <input type="email" required autoComplete="email" placeholder="Email registrado con Calins" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              <input type="password" required autoComplete="new-password" placeholder="Creá una contraseña (mín. 8)" value={pass} onChange={(e) => setPass(e.target.value)} className={inputCls} />
              <input type="password" required autoComplete="new-password" placeholder="Repetí la contraseña" value={pass2} onChange={(e) => setPass2(e.target.value)} className={inputCls} />
              <button type="submit" disabled={busy} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 transition px-4 py-3 font-semibold disabled:opacity-60">
                {busy ? 'Creando cuenta…' : 'Crear cuenta'}
              </button>
            </form>
          )}
        </div>

        <p className="text-[11px] text-slate-300 text-center mt-4 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
          ¿Sos del laboratorio? <a className="underline" href="/login">Ingresá por acá</a>.
        </p>
      </div>
    </div>
  );
}
